import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { contactsApi, bauvorhabenApi } from '@/services/api';
import { useAuth } from '@/hooks/useAuth';
import type { Contact, Proposal, ContactBauvorhabenLink } from '@/types';
import { formatTimeAgo } from "@/utils/timeAgo";
import {
  ArrowLeft,
  Phone,
  Mail,
    MapPin,
  Briefcase,
  Globe,
  Calendar,
  Loader2,
  Trash2,
  AlertCircle,
  ChevronRight,
  HardHat
} from 'lucide-react';

// Bauvorhaben-Labels — lokale Kopie (BauvorhabenDetailPage pflegt die
// kanonischen Maps; hier nur Anzeige, kein Modul-Import nötig)
const bvhStatusLabels: Record<string, string> = {
  geplant: 'Geplant',
  aktiv: 'Aktiv',
  abgeschlossen: 'Abgeschlossen',
  abgebrochen: 'Abgebrochen',
};

const bvhRoleLabels: Record<string, string> = {
  bauherr: 'Bauherr',
  gewerk: 'Gewerk',
  sonstiges: 'Sonstiges',
  vertriebler: 'Vertrieb',
};

export function ContactDetailPage({ contactId, embedded, onClose }: {
  /** Embedded-Modus (Split-View-Panel): ID kommt als Prop statt aus der Route. */
  contactId?: string;
  /** Embedded-Layout: kein „Zurück“, Löschen schließt statt zu navigieren. */
  embedded?: boolean;
  /** Embedded: nach erfolgreichem Löschen aufrufen (Tab schließen). */
  onClose?: () => void;
} = {}) {
  const routeId = useParams<{ id: string }>().id;
  const id = contactId ?? routeId;
  const navigate = useNavigate();
  const location = useLocation();
  const fromCalendar = location.state?.fromCalendar === true;
  const { isAdmin, user: _user, hasModule } = useAuth();
  const [contact, setContact] = useState<Contact | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(id === 'new');
  const [formData, setFormData] = useState<Partial<Contact>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingProposals] = useState<Proposal[]>([]);
  const [showProposalDetails, setShowProposalDetails] = useState<number | null>(null);
  const [bvhLinks, setBvhLinks] = useState<ContactBauvorhabenLink[]>([]);
  const bvhEnabled = hasModule('bauvorhaben');

  useEffect(() => {
    if (id && id !== 'new') {
      loadContact();
    } else {
      setIsLoading(false);
      setFormData({});
    }
  }, [id]);

  // Verknüpfte Bauvorhaben dieses Kontakts (lesend; Modul-gated — ohne
  // bauvorhaben-Modul wird gar nicht erst gefragt).
  useEffect(() => {
    if (!bvhEnabled || !id || id === 'new') {
      setBvhLinks([]);
      return;
    }
    let cancelled = false;
    bauvorhabenApi
      .getByContact(Number(id))
      .then((r) => { if (!cancelled) setBvhLinks(r.data.data); })
      .catch(() => { if (!cancelled) setBvhLinks([]); });
    return () => { cancelled = true; };
  }, [id, bvhEnabled]);



  const getActionText = (action: string) => {
    switch (action) {
      case "create": return "Neuer Kontakt";
      case "update": return "Aktualisierung";
      case "delete": return "Löschung";
      default: return action;
    }
  };

  const getActionColor = (action: string) => {
    switch (action) {
      case "create": return "bg-green-100 text-green-800";
      case "update": return "bg-yellow-100 text-yellow-800";
      case "delete": return "bg-red-100 text-red-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  const loadContact = async () => {
    try {
      const response = await contactsApi.getById(Number(id));
      setContact(response.data.data);
      setFormData(response.data.data);
    } catch (error) {
      console.error('Failed to load contact:', error);
      if (embedded) {
        setError('Kontakt konnte nicht geladen werden.');
      } else {
        navigate('/contacts');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    // Validate: at least one identifying field must be present
    const hasIdentifier = formData.first_name?.trim() || 
                          formData.last_name?.trim() || 
                          formData.email_address?.trim() ||
                          formData.company_name?.trim() ||
                          formData.display_name?.trim();
    if (!hasIdentifier) {
      setError('Bitte geben Sie mindestens einen Namen, eine E-Mail oder eine Firma ein.');
      setIsSubmitting(false);
      return;
    }

    try {
      // Auto-compute display_name from first_name + last_name before saving
      const displayName = [formData.first_name, formData.last_name].filter(Boolean).join(' ').trim();
      const dataToSave = { ...formData, display_name: displayName || undefined };
      
      if (id === 'new') {
        await contactsApi.create(dataToSave);
      } else {
        await contactsApi.update(Number(id), dataToSave);
      }
      if (embedded) {
        // Im Panel bleiben (kein Navigate): Ansicht schließen + neu laden.
        setIsEditing(false);
        await loadContact();
      } else {
        navigate('/contacts');
      }
    } catch (error) {
      console.error('Failed to save contact:', error);
      const err = error as { response?: { data?: { error?: string } } };
      setError(err.response?.data?.error || 'Fehler beim Speichern des Kontakts');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!confirm('Möchten Sie diesen Kontakt wirklich löschen?')) return;
    
    try {
      await contactsApi.delete(Number(id));
      if (embedded) {
        onClose?.();
        return;
      }
      navigate(fromCalendar ? '/calendar' : '/');
    } catch (error) {
      console.error('Failed to delete contact:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  // Embedded (Panel): Ladefehler als Karte zeigen statt zur Liste zu navigieren.
  if (embedded && !isEditing && !contact) {
    return (
      <div className="card p-8 text-center text-gray-500">
        <p>Kontakt konnte nicht geladen werden.</p>
      </div>
    );
  }

  if (!isEditing && contact) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          {!embedded && (
            <button
              onClick={() => navigate(fromCalendar ? '/calendar' : '/')}
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
            >
              <ArrowLeft className="h-4 w-4 mr-1" />
              Zurück
            </button>
          )}
          <div className="flex gap-2">
            <button
              onClick={() => setIsEditing(true)}
              className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
            >
              Bearbeiten
            </button>
            {isAdmin && (
              <button
                onClick={handleDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                <Trash2 className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>

        {/* Pending Proposals Warning */}
        {pendingProposals.length > 0 && (
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-yellow-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="font-medium text-yellow-800">
                  {pendingProposals.length} ausstehende Änderung{pendingProposals.length > 1 ? "en" : ""} von anderen Benutzern
                </p>
                <p className="text-sm text-yellow-700 mt-1">
                  Dieser Kontakt hat vorgeschlagene Änderungen, die noch nicht genehmigt wurden.
                </p>
                
                <div className="mt-3 space-y-2">
                  {pendingProposals.map((proposal) => (
                    <div key={proposal.id} className="bg-white rounded-md border border-yellow-200 p-3">
                      <button
                        onClick={() => setShowProposalDetails(showProposalDetails === proposal.id ? null : proposal.id)}
                        className="w-full flex items-center justify-between text-left"
                      >
                        <div className="flex items-center gap-2">
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getActionColor(proposal.action)}`}>
                            {getActionText(proposal.action)}
                          </span>
                          <span className="text-sm text-gray-600">
                            von {proposal.proposed_by_name || "Unbekannt"}
                          </span>
                          <span className="text-xs text-gray-400">
                            {formatTimeAgo(proposal.created_at)}
                          </span>
                        </div>
                        <ChevronRight 
                          className={`h-4 w-4 text-gray-400 transition-transform ${showProposalDetails === proposal.id ? "rotate-90" : ""}`} 
                        />
                      </button>
                      
                      {showProposalDetails === proposal.id && (
                        <div className="mt-3 pt-3 border-t border-gray-100">
                          <div className="text-sm">
                            {proposal.new_contact_data.first_name !== contact?.first_name && (
                              <div className="py-1">
                                <span className="text-gray-500">Vorname: </span>
                                <span className="line-through text-gray-400">{contact?.first_name}</span>
                                <span className="text-green-700 font-medium ml-1">→ {proposal.new_contact_data.first_name}</span>
                              </div>
                            )}
                            {proposal.new_contact_data.last_name !== contact?.last_name && (
                              <div className="py-1">
                                <span className="text-gray-500">Nachname: </span>
                                <span className="line-through text-gray-400">{contact?.last_name}</span>
                                <span className="text-green-700 font-medium ml-1">→ {proposal.new_contact_data.last_name}</span>
                              </div>
                            )}
                            {proposal.new_contact_data.email_address !== contact?.email_address && (
                              <div className="py-1">
                                <span className="text-gray-500">E-Mail: </span>
                                <span className="line-through text-gray-400">{contact?.email_address}</span>
                                <span className="text-green-700 font-medium ml-1">→ {proposal.new_contact_data.email_address}</span>
                              </div>
                            )}
                            {proposal.new_contact_data.company_name !== contact?.company_name && (
                              <div className="py-1">
                                <span className="text-gray-500">Firma: </span>
                                <span className="line-through text-gray-400">{contact?.company_name}</span>
                                <span className="text-green-700 font-medium ml-1">→ {proposal.new_contact_data.company_name}</span>
                              </div>
                            )}
                            {proposal.new_contact_data.mobile_phone !== contact?.mobile_phone && (
                              <div className="py-1">
                                <span className="text-gray-500">Mobil: </span>
                                <span className="line-through text-gray-400">{contact?.mobile_phone}</span>
                                <span className="text-green-700 font-medium ml-1">→ {proposal.new_contact_data.mobile_phone}</span>
                              </div>
                            )}
                          </div>
                          
                          {isAdmin && (
                            <div className="mt-3 flex gap-2">
                              <a
                                href={`/proposals/${proposal.id}`}
                                className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-primary-600 hover:text-primary-700"
                              >
                                Zum Antrag →
                              </a>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          {/* Header */}
          <h1 className="text-2xl font-bold text-gray-900">{contact.display_name}</h1>
          {(contact.job_title || contact.company_name) && (
            <div className="mt-2 flex items-center text-gray-600">
              <Briefcase className="h-5 w-5 mr-2" />
              {contact.job_title}
              {contact.job_title && contact.company_name && ' bei '}
              {contact.company_name}
            </div>
          )}

          {/* Two-column layout on large screens */}
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* LEFT COLUMN — Contact Info */}
            <div className="space-y-6">
              {/* Kontakt bucket */}
              {(contact.email_address || contact.mobile_phone || contact.business_phone || contact.business_phone_2 || contact.home_phone || contact.home_phone_2 || contact.fax || contact.pager || contact.business_homepage) && (
                <div className="bg-gray-50 rounded-lg border border-gray-100 p-4">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Kontakt</h3>
                  <div className="space-y-4">
                    {/* E-Mail */}
                    {(contact.email_address || contact.secondary_email || contact.tertiary_email) && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-400 mb-1.5">E-Mail</h4>
                        <div className="space-y-1.5">
                          {contact.email_address && (
                            <a href={`mailto:${contact.email_address}`} className="flex items-center text-sm text-primary-600 hover:text-primary-700">
                              <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                              {contact.email_address}
                            </a>
                          )}
                          {contact.secondary_email && (
                            <a href={`mailto:${contact.secondary_email}`} className="flex items-center text-sm text-primary-600 hover:text-primary-700">
                              <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                              {contact.secondary_email}
                            </a>
                          )}
                          {contact.tertiary_email && (
                            <a href={`mailto:${contact.tertiary_email}`} className="flex items-center text-sm text-primary-600 hover:text-primary-700">
                              <Mail className="h-4 w-4 mr-2 flex-shrink-0" />
                              {contact.tertiary_email}
                            </a>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Telefon */}
                    {(contact.mobile_phone || contact.business_phone || contact.business_phone_2 || contact.home_phone || contact.home_phone_2) && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-400 mb-1.5">Telefon</h4>
                        <div className="space-y-1.5">
                          {contact.mobile_phone && (
                            <a href={`tel:${contact.mobile_phone}`} className="flex items-center text-sm text-primary-600 hover:text-primary-700">
                              <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                              {contact.mobile_phone} <span className="text-gray-400 ml-1">(Mobil)</span>
                            </a>
                          )}
                          {contact.business_phone && (
                            <a href={`tel:${contact.business_phone}`} className="flex items-center text-sm text-primary-600 hover:text-primary-700">
                              <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                              {contact.business_phone} <span className="text-gray-400 ml-1">(Geschäftlich)</span>
                            </a>
                          )}
                          {contact.business_phone_2 && (
                            <a href={`tel:${contact.business_phone_2}`} className="flex items-center text-sm text-primary-600 hover:text-primary-700">
                              <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                              {contact.business_phone_2} <span className="text-gray-400 ml-1">(Geschäftlich 2)</span>
                            </a>
                          )}
                          {contact.home_phone && (
                            <a href={`tel:${contact.home_phone}`} className="flex items-center text-sm text-primary-600 hover:text-primary-700">
                              <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                              {contact.home_phone} <span className="text-gray-400 ml-1">(Privat)</span>
                            </a>
                          )}
                          {contact.home_phone_2 && (
                            <a href={`tel:${contact.home_phone_2}`} className="flex items-center text-sm text-primary-600 hover:text-primary-700">
                              <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                              {contact.home_phone_2} <span className="text-gray-400 ml-1">(Privat 2)</span>
                            </a>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Sonstiges */}
                    {(contact.fax || contact.pager || contact.business_homepage) && (
                      <div>
                        <h4 className="text-xs font-medium text-gray-400 mb-1.5">Sonstiges</h4>
                        <div className="space-y-1.5">
                          {contact.fax && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                              {contact.fax} <span className="text-gray-400 ml-1">(Fax)</span>
                            </div>
                          )}
                          {contact.pager && (
                            <div className="flex items-center text-sm text-gray-600">
                              <Phone className="h-4 w-4 mr-2 flex-shrink-0" />
                              {contact.pager} <span className="text-gray-400 ml-1">(Pager)</span>
                            </div>
                          )}
                          {contact.business_homepage && (
                            <a href={contact.business_homepage} target="_blank" rel="noopener" className="flex items-center text-sm text-primary-600 hover:text-primary-700">
                              <Globe className="h-4 w-4 mr-2 flex-shrink-0" />
                              {contact.business_homepage}
                            </a>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Geburtstag */}
              {contact.birthday && (
                <div className="bg-gray-50 rounded-lg border border-gray-100 p-4">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Geburtstag</h3>
                  <div className="flex items-center text-sm text-gray-600">
                    <Calendar className="h-4 w-4 mr-2 flex-shrink-0" />
                    {new Date(contact.birthday).toLocaleDateString('de-DE')}
                  </div>
                </div>
              )}

              {/* Notizen */}
              {contact.personal_notes && (
                <div className="bg-gray-50 rounded-lg border border-gray-100 p-4">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-2">Notizen</h3>
                  <p className="text-sm text-gray-600 whitespace-pre-wrap">{contact.personal_notes}</p>
                </div>
              )}
            </div>

            {/* RIGHT COLUMN — Addresses */}
            <div className="space-y-6">
              {/* Bauvorhaben Adresse — separate, prominent bucket for project managers */}
              {(contact.other_street || contact.other_city) && (
                <div className="bg-orange-50 rounded-lg border border-orange-200 p-4 shadow-sm">
                  <h3 className="text-xs font-bold text-orange-700 uppercase tracking-wider mb-3 flex items-center">
                    <MapPin className="h-4 w-4 mr-1.5 text-orange-500" />
                    Bauvorhaben Adresse
                  </h3>
                  <div className="text-sm text-gray-700">
                    {contact.other_street && <p className="font-medium">{contact.other_street}</p>}
                    <p>{contact.other_postal_code} {contact.other_city}</p>
                    {(contact.other_state || contact.other_country) && <p>{[contact.other_state, contact.other_country].filter(Boolean).join(', ')}</p>}
                  </div>
                </div>
              )}

              {/* Bauvorhaben, denen dieser Kontakt zugeordnet ist (lesend) */}
              {bvhLinks.length > 0 && (
                <div className="bg-gray-50 rounded-lg border border-gray-100 p-4">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3 flex items-center">
                    <HardHat className="h-4 w-4 mr-1.5 text-gray-400" />
                    Bauvorhaben
                  </h3>
                  <div className="space-y-2">
                    {bvhLinks.map((l) => (
                      <Link
                        key={`${l.id}-${l.role}-${l.gewerk_label}`}
                        to={`/bauvorhaben/${l.id}`}
                        className="flex items-center justify-between gap-2 text-sm text-primary-600 hover:text-primary-700"
                      >
                        <span className="font-medium truncate">{l.name}</span>
                        <span className="flex items-center gap-2 shrink-0">
                          <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-600">
                            {l.role === 'gewerk' && l.gewerk_label
                              ? `Gewerk: ${l.gewerk_label}`
                              : bvhRoleLabels[l.role] ?? l.role}
                          </span>
                          <span className="text-xs text-gray-500">{bvhStatusLabels[l.status] ?? l.status}</span>
                        </span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}

              {/* Geschäftsadresse + Privatadresse */}
              {(contact.street || contact.city || contact.home_street || contact.home_city) && (
                <div className="bg-gray-50 rounded-lg border border-gray-100 p-4">
                  <h3 className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">Adressen</h3>
                  <div className="space-y-4">
                    {(contact.street || contact.city) && (
                      <div className="pl-3 border-l-2 border-blue-400">
                        <h4 className="text-sm font-medium text-gray-700 flex items-center">
                          <MapPin className="h-3 w-3 mr-1 text-blue-400" />
                          Geschäftsadresse
                        </h4>
                        <div className="mt-1 text-sm text-gray-600">
                          {contact.street && <p>{contact.street}</p>}
                          <p>{contact.postal_code} {contact.city}</p>
                          {(contact.state || contact.country) && <p>{[contact.state, contact.country].filter(Boolean).join(', ')}</p>}
                        </div>
                      </div>
                    )}
                    {(contact.home_street || contact.home_city) && (
                      <div className="pl-3 border-l-2 border-green-400">
                        <h4 className="text-sm font-medium text-gray-700 flex items-center">
                          <MapPin className="h-3 w-3 mr-1 text-green-400" />
                          Privatadresse
                        </h4>
                        <div className="mt-1 text-sm text-gray-600">
                          {contact.home_street && <p>{contact.home_street}</p>}
                          <p>{contact.home_postal_code} {contact.home_city}</p>
                          {(contact.home_state || contact.home_country) && <p>{[contact.home_state, contact.home_country].filter(Boolean).join(', ')}</p>}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center">
        <button
          onClick={() => id === 'new' ? navigate(fromCalendar ? '/calendar' : '/') : setIsEditing(false)}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700"
        >
          <ArrowLeft className="h-4 w-4 mr-1" />
          Zurück
        </button>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md">
          {error}
        </div>
      )}

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        <h1 className="text-xl font-bold text-gray-900">{id === 'new' ? 'Neuer Kontakt' : 'Kontakt bearbeiten'}</h1>

        {/* Name Section */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Name</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Anrede</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
                placeholder="z.B. Dr."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Vorname</label>
              <input
                type="text"
                value={formData.first_name || ''}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Nachname</label>
              <input
                type="text"
                value={formData.last_name || ''}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-500">Anzeigename</label>
              <input
                type="text"
                disabled
                value={[formData.first_name, formData.last_name].filter(Boolean).join(' ').trim() || ''}
                className="mt-1 block w-full rounded-md border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed shadow-sm"
              />
              <p className="mt-1 text-xs text-gray-400">Automatisch aus Vor- und Nachname</p>
            </div>
          </div>
        </div>

        {/* Company Section */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Unternehmen</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Firma</label>
              <input
                type="text"
                value={formData.company_name || ''}
                onChange={(e) => setFormData({ ...formData, company_name: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Abteilung</label>
              <input
                type="text"
                value={formData.department || ''}
                onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Position</label>
              <input
                type="text"
                value={formData.job_title || ''}
                onChange={(e) => setFormData({ ...formData, job_title: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
          </div>
        </div>

        {/* Emails Section */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">E-Mail Adressen</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Primäre E-Mail</label>
              <input
                type="email"
                value={formData.email_address || ''}
                onChange={(e) => setFormData({ ...formData, email_address: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Sekundäre E-Mail</label>
              <input
                type="email"
                value={formData.secondary_email || ''}
                onChange={(e) => setFormData({ ...formData, secondary_email: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Weitere E-Mail</label>
              <input
                type="email"
                value={formData.tertiary_email || ''}
                onChange={(e) => setFormData({ ...formData, tertiary_email: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
          </div>
        </div>

        {/* Phones Section */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Telefonnummern</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Mobil</label>
              <input
                type="tel"
                value={formData.mobile_phone || ''}
                onChange={(e) => setFormData({ ...formData, mobile_phone: e.target.value.replace(/[^0-9+]/g, '') })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Geschäftlich</label>
              <input
                type="tel"
                value={formData.business_phone || ''}
                onChange={(e) => setFormData({ ...formData, business_phone: e.target.value.replace(/[^0-9+]/g, '') })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Geschäftlich 2</label>
              <input
                type="tel"
                value={formData.business_phone_2 || ''}
                onChange={(e) => setFormData({ ...formData, business_phone_2: e.target.value.replace(/[^0-9+]/g, '') })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Privat</label>
              <input
                type="tel"
                value={formData.home_phone || ''}
                onChange={(e) => setFormData({ ...formData, home_phone: e.target.value.replace(/[^0-9+]/g, '') })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
          </div>
        </div>

        {/* Bauvorhaben Address */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Bauvorhaben Adresse</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Straße</label>
              <input
                type="text"
                value={formData.other_street || ''}
                onChange={(e) => setFormData({ ...formData, other_street: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">PLZ</label>
              <input
                type="text"
                value={formData.other_postal_code || ''}
                onChange={(e) => setFormData({ ...formData, other_postal_code: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Stadt</label>
              <input
                type="text"
                value={formData.other_city || ''}
                onChange={(e) => setFormData({ ...formData, other_city: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Bundesland</label>
              <input
                type="text"
                value={formData.other_state || ''}
                onChange={(e) => setFormData({ ...formData, other_state: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Land</label>
              <input
                type="text"
                value={formData.other_country || ''}
                onChange={(e) => setFormData({ ...formData, other_country: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
          </div>
        </div>

        {/* Business Address */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Geschäftsadresse</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Straße</label>
              <input
                type="text"
                value={formData.street || ''}
                onChange={(e) => setFormData({ ...formData, street: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">PLZ</label>
              <input
                type="text"
                value={formData.postal_code || ''}
                onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Stadt</label>
              <input
                type="text"
                value={formData.city || ''}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Bundesland</label>
              <input
                type="text"
                value={formData.state || ''}
                onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Land</label>
              <input
                type="text"
                value={formData.country || ''}
                onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
          </div>
        </div>

        {/* Home Address */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Privatadresse</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-gray-700">Straße</label>
              <input
                type="text"
                value={formData.home_street || ''}
                onChange={(e) => setFormData({ ...formData, home_street: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">PLZ</label>
              <input
                type="text"
                value={formData.home_postal_code || ''}
                onChange={(e) => setFormData({ ...formData, home_postal_code: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Stadt</label>
              <input
                type="text"
                value={formData.home_city || ''}
                onChange={(e) => setFormData({ ...formData, home_city: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Land</label>
              <input
                type="text"
                value={formData.home_country || ''}
                onChange={(e) => setFormData({ ...formData, home_country: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
          </div>
        </div>

        {/* Other Info */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Weitere Informationen</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Webseite</label>
              <input
                type="url"
                value={formData.business_homepage || ''}
                onChange={(e) => setFormData({ ...formData, business_homepage: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700">Geburtstag</label>
              <input
                type="date"
                value={formData.birthday || ''}
                onChange={(e) => setFormData({ ...formData, birthday: e.target.value })}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700">Notizen</label>
            <textarea
              rows={4}
              value={formData.personal_notes || ''}
              onChange={(e) => setFormData({ ...formData, personal_notes: e.target.value })}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-600 focus:ring-primary-600"
            />
          </div>
        </div>

        <div className="flex gap-4 pt-4 border-t">
          <button
            type="submit"
            disabled={isSubmitting}
            className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 disabled:opacity-50"
          >
            {isSubmitting ? 'Speichern...' : 'Zur Genehmigung einreichen'}
          </button>
          
          <button
            type="button"
            onClick={() => id === 'new' ? navigate(fromCalendar ? '/calendar' : '/') : setIsEditing(false)}
            className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 hover:bg-gray-50"
          >
            Abbrechen
          </button>
        </div>
      </form>
    </div>
  );
}
